import {
  NOTE_ATTACHMENT_PROFILE,
  buildBlockedAttachmentsMessage,
  validateAttachments,
} from '../../utils/attachment-validation.util';

/**
 * Shapes shared by the note-attachment picker and list, plus the mappers that
 * get every host onto one of them.
 *
 * The app has to render three kinds of attachment row and they are NOT the same
 * shape on the wire:
 *
 *   1. a saved `note_attachment` row, addressable by its own id;
 *   2. a pre-rollout note whose single file lives on the note's own
 *      `original_file_name` / `system_generated_file_name` columns and therefore
 *      has no attachment id at all;
 *   3. an unsaved `File` on a draft note (create-account / create-contact),
 *      which has no server presence whatsoever.
 *
 * Without one view-model every host would branch on all three. `NoteAttachmentView`
 * is that model, and `legacy` / `local` are how a row says which of the three it
 * came from so the host can route preview/download/delete correctly.
 *
 * All three render as the SAME pill — see NoteAttachmentListComponent. The row's
 * provenance changes where its actions go, never how it looks.
 */


/** A saved attachment as the API returns it (`expands=attachments`). */
export interface NoteAttachment {
  id: number | null;
  note_id?: number;
  original_file_name: string;
  system_generated_file_name?: string | null;
  mime_type?: string | null;
  /** Bytes. Null on rows backfilled from the note columns — never recorded there. */
  file_size?: number | null;
  created_at?: string | null;
  /** Set by `normaliseNoteAttachments` on a synthesised pre-rollout row. */
  legacy?: boolean;
}

/** One row, whatever it came from. */
export interface NoteAttachmentView {
  /** `note_attachment.id`. Null for a legacy mirror row and for a local draft file. */
  id: number | null;
  name: string;
  /** Bytes, or null when unknown (backfilled rows never recorded it). */
  size: number | null;
  mimeType: string | null;
  /** Set only for an unsaved draft file — preview/delete resolve locally. */
  local?: File;
  /**
   * Set only for a pre-rollout note whose file lives on the note columns. The
   * per-attachment endpoints cannot address it (there is no attachment id), so
   * the host must route it to the deprecated note-scoped shim instead.
   */
  legacy?: boolean;
  /** The owning note's id — what a legacy row needs to reach the note-scoped shim. */
  noteId?: number | null;
}

/**
 * Notes written before the multi-attachment rollout carry the file on the note
 * itself; newer ones carry `attachments[]`. Normalise both to `attachments[]` so
 * every template reads one shape.
 *
 * The synthesised row has NO id, so the per-attachment endpoints cannot address
 * it — `legacy: true` tells the host to fall back to the note-scoped
 * preview/download/delete shims for that one row.
 *
 * In practice the API's Phase 1 lands first, so `attachments` is always present
 * by the time this ships. The fallback exists so an ordering slip degrades to
 * TODAY's behaviour rather than to a note that appears to have lost its file.
 * Remove it alongside the API's deprecated note-scoped routes.
 */
export function normaliseNoteAttachments(note: any): NoteAttachment[] {
  if (Array.isArray(note?.attachments)) return note.attachments;
  if (!note?.original_file_name) return [];
  return [{
    id: null,
    note_id: note.id,
    legacy: true,
    original_file_name: note.original_file_name,
    system_generated_file_name: note.system_generated_file_name ?? null,
    mime_type: null,
    file_size: null,
    created_at: note.created_at ?? null,
  }];
}

/**
 * Normalise a note AND its replies in place, so a caller does it once per fetch
 * rather than per render. Mutating is deliberate: the note objects are what the
 * templates iterate and what the delete paths splice, so a copy would leave the
 * rendered list and the mutated one out of step.
 */
export function normaliseNoteTree(notes: any[] | null | undefined): void {
  for (const note of notes ?? []) {
    if (!note) continue;
    note.attachments = normaliseNoteAttachments(note);
    for (const reply of note.childNote ?? []) {
      if (reply) reply.attachments = normaliseNoteAttachments(reply);
    }
  }
}

/** Saved attachments → rows. */
export function toViews(attachments: NoteAttachment[] | null | undefined, noteId?: number | null): NoteAttachmentView[] {
  return (attachments ?? []).map((a) => ({
    id: a.id ?? null,
    name: a.original_file_name,
    size: a.file_size ?? null,
    mimeType: a.mime_type ?? null,
    legacy: a.legacy === true || a.id == null,
    noteId: a.note_id ?? noteId ?? null,
  }));
}

/** Unsaved draft `File`s → rows. */
export function toLocalViews(files: File[] | null | undefined): NoteAttachmentView[] {
  return (files ?? []).map((f) => ({
    id: null,
    name: f.name,
    size: f.size,
    mimeType: f.type || null,
    local: f,
  }));
}

/**
 * The one place a batch of picked/dropped files is turned into a new selection.
 *
 * The picker uses it, and so does any host whose drop target it does not own —
 * `internal-notes` drops onto the Quill editor container, which sits outside the
 * picker's template. Both paths therefore apply the same 40MB-per-file rule, the
 * same duplicate suppression and the same message, instead of the host
 * re-implementing a looser version of it (which is what every surface did before
 * this feature).
 *
 * The only cap is per file, so nothing about what is already on the note changes
 * the verdict on a new one — which is why this takes no `existingCount` /
 * `existingBytes`. It used to, to feed a 10-file and 100MB-total cap; both are
 * gone, here and on the API.
 *
 * Returns the message rather than raising it, so the caller keeps using its own
 * AlertService instance and its own toast conventions.
 *
 * @param picked  Newly picked or dropped files.
 * @param current The selection so far — not mutated. Feeds duplicate detection.
 */
export function acceptNoteFiles(
  picked: ArrayLike<File> | null | undefined,
  current: File[],
  /**
   * Attachments already SAVED on the note being edited. They occupy slots
   * against the profile's 10-file cap even though they are not `File`s here, so
   * a note holding 9 stored files accepts one more pick and refuses the rest.
   * Defaults to none — a brand-new note or reply has nothing stored yet.
   */
  savedCount: number = 0,
): { files: File[]; error: string } {
  if (!picked || picked.length === 0) {
    return { files: current, error: '' };
  }

  const { accepted, rejected } = validateAttachments(
    picked,
    current,
    0,
    NOTE_ATTACHMENT_PROFILE,
    savedCount,
  );

  return {
    // A NEW array whenever anything was accepted, so a host behind OnPush
    // re-renders; the same reference when nothing was, so nothing repaints for a
    // batch that was entirely rejected.
    files: accepted.length ? [...current, ...accepted] : current,
    error: rejected.length ? buildBlockedAttachmentsMessage(rejected, NOTE_ATTACHMENT_PROFILE) : '',
  };
}

/**
 * Coerce whatever a draft-note dialog handed back into a plain `File[]`.
 *
 * The add-note dialogs used to close with `attach_document` holding either a
 * `File` or a `FileHandle` (`{file, url}`) depending on whether the file was
 * picked or dropped, which is why their callers all tested `?.file?.name` first.
 * They now close with `attach_documents: File[]`, but a host may still receive
 * the old single-value shape from a dialog that has not been migrated — so this
 * accepts all three and always yields an array.
 */
export function toFileArray(value: any): File[] {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((item: any) => (item instanceof File ? item : item?.file))
    .filter((f: any): f is File => !!f);
}

/**
 * Splice one attachment out of the note that holds it, in place.
 *
 * Used by every host's delete path so the row disappears without a full reload —
 * a reload would collapse an open reply composer and lose its draft text, and the
 * only thing that changed is one row.
 *
 * Matched by attachment id where there is one and by name otherwise (a legacy row
 * has no id, and such a note has only the one file by definition). The deprecated
 * `original_file_name` / `system_generated_file_name` mirrors are re-pointed at
 * whatever is now first — the API does the same server-side, and leaving a stale
 * mirror here would let `normaliseNoteAttachments` resurrect the deleted row.
 */
export function removeAttachmentFrom(note: any, row: NoteAttachmentView | null | undefined): void {
  if (!note) return;
  const list: any[] = Array.isArray(note.attachments)
    ? note.attachments
    : normaliseNoteAttachments(note);
  const index = row?.id != null
    ? list.findIndex((a) => a?.id === row.id)
    : list.findIndex((a) => a?.original_file_name === row?.name);
  if (index >= 0) {
    list.splice(index, 1);
  }
  note.attachments = list;
  note.original_file_name = list[0]?.original_file_name ?? null;
  note.system_generated_file_name = list[0]?.system_generated_file_name ?? null;
}

/**
 * `"3 files attached"` / `"1 file attached"` — the live attachment count every
 * note surface renders beside its picker.
 *
 * It is console-v2's `attachedCount()` (`shared/attachments.ts`), wording and
 * pluralisation unchanged, and it lives HERE — beside the row helpers — so that
 * every surface counts the same way. The composer and the Add/Edit Note dialog
 * hold their attachments in different shapes (the composer merges
 * `savedAttachments` and picked `File`s into one `rows` list; the dialog keeps
 * `savedViews` and `pickedViews` separate because their ✕ do different things),
 * and each was one splice away from growing its own tally. They pass their
 * TOTAL through this instead.
 *
 * Takes a count rather than a list precisely because of that second case: a
 * surface holding two lists must add them up before it can be counted, and
 * handing this the two arrays would have made it the place where "which lists
 * count" is decided, which is the host's business.
 */
export function attachedCount(count: number): string {
  const n = Math.max(0, count ?? 0);
  return `${n} ${n === 1 ? 'file' : 'files'} attached`;
}
